import { api } from "./api";

export type AssetClass =
  | "LIQUIDE"
  | "ANLAGEN"
  | "IMMOBILIE"
  | "SONSTIGE"
  | "VERBINDLICHKEITEN";

export type InsuranceKind =
  | "KV"
  | "HAFTPFLICHT"
  | "HAUSRAT"
  | "BU"
  | "LV"
  | "AUTO"
  | "RECHTSSCHUTZ"
  | "REISE"
  | "SONSTIGE";

export type InsuranceRhythm = "MONATLICH" | "QUARTALSWEISE" | "HALBJAEHRLICH" | "JAEHRLICH";

export interface InsurancePremium {
  id: string;
  insuranceId: string;
  validFrom: string;
  amountCents: number;
  rhythm: InsuranceRhythm;
  notes: string | null;
  cashflowPositionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Insurance {
  id: string;
  name: string;
  kind: InsuranceKind;
  insurer: string;
  contractNumber: string | null;
  startDate: string;
  endDate: string | null;
  noticePeriodDays: number | null;
  coverageSumCents: number | null;
  deductibleCents: number | null;
  notes: string | null;
  tags: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  valueAssetClass: AssetClass | null;
  currentValueCents: number | null;
  currentPremium: InsurancePremium | null;
  monthlyEquivalentCents: number | null;
  nextRenewalDate: string | null;
  noticeDeadlineDate: string | null;
}

export interface InsuranceValueSnapshot {
  id: string;
  insuranceId: string;
  asOf: string;
  valueCents: number;
  source: string;
  notes: string | null;
  createdAt: string;
}

export interface AddValueSnapshotInput {
  asOf: string;
  valueCents: number;
  source?: "MANUAL" | "IMPORT" | "API";
  notes?: string;
}

export interface InsuranceDetail extends Insurance {
  premiums: InsurancePremium[];
  valueSnapshots: InsuranceValueSnapshot[];
}

export interface InsuranceSummary {
  activeCount: number;
  totalMonthlyEquivalentCents: number;
  totalYearlyEquivalentCents: number;
}

export interface CreateInsuranceInput {
  name: string;
  kind: InsuranceKind;
  insurer: string;
  contractNumber?: string;
  startDate: string;
  endDate?: string;
  noticePeriodDays?: number;
  coverageSumCents?: number;
  deductibleCents?: number;
  notes?: string;
  tags?: string[];
  valueAssetClass?: AssetClass;
  initialPremium: {
    validFrom: string;
    amountCents: number;
    rhythm: InsuranceRhythm;
    notes?: string;
  };
}

export type UpdateInsuranceInput = Partial<{
  name: string;
  kind: InsuranceKind;
  insurer: string;
  contractNumber: string | null;
  startDate: string;
  endDate: string | null;
  noticePeriodDays: number | null;
  coverageSumCents: number | null;
  deductibleCents: number | null;
  notes: string | null;
  tags: string[];
  isActive: boolean;
  valueAssetClass: AssetClass | null;
}>;

export interface AddPremiumInput {
  validFrom: string;
  amountCents: number;
  rhythm: InsuranceRhythm;
  notes?: string;
}

export type UpdatePremiumInput = Partial<{
  validFrom: string;
  amountCents: number;
  rhythm: InsuranceRhythm;
  notes: string | null;
}>;

export interface ListInsurancesFilters {
  active?: boolean;
  kind?: InsuranceKind;
}

function qs(filters: ListInsurancesFilters): string {
  const sp = new URLSearchParams();
  if (filters.active !== undefined) sp.set("active", String(filters.active));
  if (filters.kind) sp.set("kind", filters.kind);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const insuranceApi = {
  list: (filters: ListInsurancesFilters = {}) =>
    api<{ insurances: Insurance[] }>(`/insurance${qs(filters)}`),
  summary: () => api<InsuranceSummary>("/insurance/summary"),
  get: (id: string) => api<InsuranceDetail>(`/insurance/${id}`),
  create: (input: CreateInsuranceInput) =>
    api<InsuranceDetail>("/insurance", { method: "POST", body: input }),
  update: (id: string, input: UpdateInsuranceInput) =>
    api<InsuranceDetail>(`/insurance/${id}`, { method: "PATCH", body: input }),
  softDelete: (id: string) => api<void>(`/insurance/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) => api<void>(`/insurance/${id}/hard`, { method: "DELETE" }),
  addPremium: (id: string, input: AddPremiumInput) =>
    api<InsuranceDetail>(`/insurance/${id}/premiums`, { method: "POST", body: input }),
  deletePremium: (id: string, premiumId: string) =>
    api<void>(`/insurance/${id}/premiums/${premiumId}`, { method: "DELETE" }),
  addValueSnapshot: (id: string, input: AddValueSnapshotInput) =>
    api<InsuranceValueSnapshot>(`/insurance/${id}/value-snapshots`, {
      method: "POST",
      body: input,
    }),
  deleteValueSnapshot: (id: string, snapshotId: string) =>
    api<void>(`/insurance/${id}/value-snapshots/${snapshotId}`, { method: "DELETE" }),
};
